import {Component, Inject, OnInit} from '@angular/core';
import {PoolEditViewService} from '../services/pool-edit-view.service';
import {DynamicFormConfig, DynamicFormReadyEvent, MultiOptionConfig} from '@mydocma/dynamic-form';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BehaviorSubject} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {PromptDialogService} from '@mydocma/component-library/src/lib/prompt-dialog';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef
} from '@angular/material/legacy-dialog';
import {AppContainerService} from '@mydocma/app-container';
import {EntityScopeEnum} from '@docma-mm-core/entity/entity-scope.enum';
import {Project} from '@docma-mm-core/project/project';
import {ICreatePoolPayload, IUpdatePoolPayload} from '../services/pool-api.service';
import {PoolVisibilityEnum, PoolVisibilityTextEnum} from '../services/pool-form.service';
import {Pool} from '@docma-mm-core/pool/pool';
import {toPoolVisibleForValue} from '../pool-left-pane/pool-left-pane.component';
import {FieldHint} from '@mydocma/dynamic-form/lib/hint-text/field-hint';


export interface IPoolEditDialogData {
  scope: EntityScopeEnum;
  project: Project;
  user: any;
  poolToEdit?: Pool;
  currentUser?: any;
}

@UntilDestroy()
@Component({
  selector: 'mydocma-mm-pool-edit',
  templateUrl: './pool-edit.component.html',
  styleUrls: ['./pool-edit.component.scss']
})
export class PoolEditComponent implements OnInit {

  formConfig: DynamicFormConfig;
  formReadyEvent: DynamicFormReadyEvent;

  hasChangesSubject = new BehaviorSubject<boolean>(false);
  hasChanges$ = this.hasChangesSubject.asObservable();

  hasErrorsSubject = new BehaviorSubject<boolean>(false);
  hasErrors$ = this.hasErrorsSubject.asObservable();

  loadingSubject = new BehaviorSubject<boolean>(false);
  loading$ = this.loadingSubject.asObservable();

  titleSubject = new BehaviorSubject<string>('some title');
  title$ = this.titleSubject.asObservable();

  saving = false;

  private lastValidatedTypeValue: EntityScopeEnum | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: IPoolEditDialogData,
    private dialogRef: MatDialogRef<PoolEditComponent>,
    private poolEditViewService: PoolEditViewService,
    private readonly translateService: TranslateService,
    private readonly dialogService: PromptDialogService,
    public dialog: MatDialog,
    public appContainerService: AppContainerService,
  ) {
    if (this.dialogData.poolToEdit) {
      const editTitle = this.translateService.instant('MM.pool.leftPane.form.editTitle', { poolName: this.dialogData.poolToEdit.Name });
      this.titleSubject.next(editTitle);
    } else {
      this.titleSubject.next(this.translateService.instant('MM.pool.leftPane.form.title'));
    }

    this.poolEditViewService.poolSaving$.pipe(untilDestroyed(this)).subscribe(saving => {
      this.saving = saving;
    });

  }

  ngOnInit(): void {
    this.formConfig = this.poolEditViewService.getPoolFormConfig(this.dialogData);
  }

  setVisibleForHint(visibleForFieldConfig: any, showHint: boolean) {
    if (showHint) {
      visibleForFieldConfig.hint = {
        text: 'MM.pool.leftPane.form.visibleForPoolWarning',
        icon: {
          iconName: '_info',
          outlined: true
        }
      } as FieldHint;
    } else {
      visibleForFieldConfig.hint = null;
    }
  }

  onFormReady(event: DynamicFormReadyEvent) {
    this.formReadyEvent = event;

    const visibleForFieldConfig = this.formReadyEvent.dynamicFormService.getFieldConfig('visibleFor');
    this.setVisibleForHint(visibleForFieldConfig, (this.dialogData.poolToEdit ? this.dialogData.poolToEdit.IsPublic : this.dialogData.scope === EntityScopeEnum.Project));
    this.formReadyEvent.dynamicFormService.updateFieldConfig(visibleForFieldConfig);

    this.formReadyEvent.dynamicFormValidationService.formErrors$
      .pipe(untilDestroyed(this))
      .subscribe(err => {
        this.hasErrorsSubject.next((Object.keys(err).length === 0 ? false : true));
      });

    this.formReadyEvent.dynamicFormEventsService.onFieldValueChanged
      .pipe(untilDestroyed(this))
      .subscribe((param: any) => {

        if (param.config.id === 'type') {
          const formScope = param.value as EntityScopeEnum;
          const isPublic = formScope === EntityScopeEnum.Project;
          this.setVisibleForHint(visibleForFieldConfig, isPublic);
          const visibleForConfig = this.formReadyEvent.dynamicFormService.getFieldConfig('visibleFor') as MultiOptionConfig;
          visibleForConfig.hidden = !isPublic;
          this.poolEditViewService.poolFormService.setVisibleForValidator(isPublic, visibleForConfig);
          this.formReadyEvent.dynamicFormService.updateFieldConfig(visibleForConfig);

          this.formReadyEvent.dynamicFormService.updateFieldValue('visibleFor', {
            sc: true,
            co: true,
            ct: true
          });

          this.validateTypeChange(param.value);
        }

        this.hasChanges();
      });

    if (this.dialogData.poolToEdit) {
      this.setCorrectVisibilityValues();
    }

    setTimeout(() => {
      this.hasChanges();

      if (this.dialogData.poolToEdit) {
        const currentTypeValue = this.formReadyEvent.dynamicFormService.getFieldValue('type');
        this.validateTypeChange(currentTypeValue);
      }
    }, 0);
  }

  private setCorrectVisibilityValues() {
    if (this.dialogData.poolToEdit && this.dialogData.poolToEdit.IsPublic && this.formReadyEvent) {
      let correctVisibilityValue: any;

      if (this.dialogData.poolToEdit.VisibleFor !== undefined && this.dialogData.poolToEdit.VisibleFor !== null) {
        if (typeof this.dialogData.poolToEdit.VisibleFor === 'number') {
          correctVisibilityValue = {
            sc: (this.dialogData.poolToEdit.VisibleFor & 4) === 4,  // Customer = 4
            co: (this.dialogData.poolToEdit.VisibleFor & 2) === 2,  // Contractor = 2
            ct: (this.dialogData.poolToEdit.VisibleFor & 1) === 1   // SubContractor = 1
          };
        } else {
          correctVisibilityValue = {
            sc: this.dialogData.poolToEdit.VisibleFor.SC || false,
            co: this.dialogData.poolToEdit.VisibleFor.CO || false,
            ct: this.dialogData.poolToEdit.VisibleFor.CT || false
          };
        }
      } else if (this.dialogData.poolToEdit.VisibleFor === 0) {
        correctVisibilityValue = {
          sc: false,
          co: false,
          ct: false
        };
      } else {
        correctVisibilityValue = {
          sc: true,
          co: true,
          ct: true
        };
      }

      this.formReadyEvent.dynamicFormService.updateFieldValue('visibleFor', correctVisibilityValue);
    }
  }

  onSaveChanges() {
    const formScope = this.formReadyEvent.dynamicFormService.getFieldValue('type') as EntityScopeEnum;
    const isPublic = formScope === EntityScopeEnum.Project;
    let poolVisibility = 0;

    if (isPublic) {
      const publicSwitches = this.formReadyEvent.dynamicFormService.getFieldValue('visibleFor');

      if (publicSwitches) {
        if (publicSwitches.sc) {
          poolVisibility += this.poolEditViewService.getPoolVisibilityEnumFromText(PoolVisibilityTextEnum.Customer);
        }
        if (publicSwitches.co) {
          poolVisibility += this.poolEditViewService.getPoolVisibilityEnumFromText(PoolVisibilityTextEnum.Contractor);
        }
        if (publicSwitches.ct) {
          poolVisibility += this.poolEditViewService.getPoolVisibilityEnumFromText(PoolVisibilityTextEnum.SubContractor);
        }
      }
    } else {
      poolVisibility = PoolVisibilityEnum.Customer + PoolVisibilityEnum.Contractor + PoolVisibilityEnum.SubContractor;
    }


    if (!this.dialogData.poolToEdit) {
      const newPoolPayload: ICreatePoolPayload = {
        Name: this.formReadyEvent.dynamicFormService.getFieldValue('name'),
        Comment: this.formReadyEvent.dynamicFormService.getFieldValue('comment'),
        IsPublic: isPublic,
        VisibleFor: poolVisibility
      };
      this.poolEditViewService.addPool(newPoolPayload, this.dialogData.project);
    } else {
      const updatePoolPayload: IUpdatePoolPayload = {
        Name: this.formReadyEvent.dynamicFormService.getFieldValue('name'),
        Comment: this.formReadyEvent.dynamicFormService.getFieldValue('comment'),
        IsPublic: isPublic,
        VisibleFor: toPoolVisibleForValue(poolVisibility)
      };
      this.poolEditViewService.updatePoolWithFormData(updatePoolPayload, this.dialogData.project, this.dialogData.poolToEdit);
    }
  }

  async onCancel() {
    const hasChangesResult = await this.hasChanges();

    if (hasChangesResult) {
      const title = await this.translateService.get('MM.dialog.discardChanges.title').toPromise();
      const content = await this.translateService.get('MM.dialog.discardChanges.content').toPromise();

      if (!(await this.dialogService.showDiscardChangesConfirmationDialog({ title, content }))?.value) {
        return;
      }
    }

    this.dialogRef.close();
  }

  async hasChanges() {
    let hasChanges = false;

    if (!this.dialogData.poolToEdit) {
      const hasNameOrComment = !!(this.formReadyEvent?.dynamicFormService.getFieldValue('name') ||
        this.formReadyEvent?.dynamicFormService.getFieldValue('comment'));

      let hasVisibilityChanges = false;
      const currentType = this.formReadyEvent?.dynamicFormService.getFieldValue('type');
      const currentIsPublic = currentType === EntityScopeEnum.Project;

      if (currentIsPublic) {
        const currentVisibleFor = this.formReadyEvent?.dynamicFormService.getFieldValue('visibleFor');
        const defaultVisibility = { sc: true, co: true, ct: true };

        if (currentVisibleFor) {
          hasVisibilityChanges = currentVisibleFor.sc !== defaultVisibility.sc ||
            currentVisibleFor.co !== defaultVisibility.co ||
            currentVisibleFor.ct !== defaultVisibility.ct;
        }
      }

      hasChanges = hasNameOrComment || hasVisibilityChanges;
    } else {
      if (this.formReadyEvent) {
        const currentName = this.formReadyEvent.dynamicFormService.getFieldValue('name');
        const currentComment = this.formReadyEvent.dynamicFormService.getFieldValue('comment');
        const currentType = this.formReadyEvent.dynamicFormService.getFieldValue('type');
        const currentVisibleFor = this.formReadyEvent.dynamicFormService.getFieldValue('visibleFor');

        const currentIsPublic = currentType === EntityScopeEnum.Project;

        const originalComment = this.dialogData.poolToEdit.Comment || '';

        const nameChanged = (currentName || '') !== (this.dialogData.poolToEdit.Name || '');
        const commentChanged = (currentComment || '') !== originalComment;
        const typeChanged = currentIsPublic !== this.dialogData.poolToEdit.IsPublic;

        if (nameChanged || commentChanged || typeChanged) {
          hasChanges = true;
        }

        if (currentIsPublic) {
          let originalVisibility: any;


          if (this.dialogData.poolToEdit.IsPublic && this.dialogData.poolToEdit.VisibleFor !== undefined && this.dialogData.poolToEdit.VisibleFor !== null) {
            if (typeof this.dialogData.poolToEdit.VisibleFor === 'number') {
              originalVisibility = {
                sc: (this.dialogData.poolToEdit.VisibleFor & 4) === 4,  // Customer = 4
                co: (this.dialogData.poolToEdit.VisibleFor & 2) === 2,  // Contractor = 2
                ct: (this.dialogData.poolToEdit.VisibleFor & 1) === 1   // SubContractor = 1
              };
            } else {
              originalVisibility = {
                sc: this.dialogData.poolToEdit.VisibleFor.SC || false,
                co: this.dialogData.poolToEdit.VisibleFor.CO || false,
                ct: this.dialogData.poolToEdit.VisibleFor.CT || false
              };
            }
          } else if (this.dialogData.poolToEdit.IsPublic && this.dialogData.poolToEdit.VisibleFor === 0) {
            originalVisibility = {
              sc: false,
              co: false,
              ct: false
            };
          } else {
            originalVisibility = {
              sc: true,
              co: true,
              ct: true
            };
          }

          const visibilityChanged = currentVisibleFor?.sc !== originalVisibility.sc ||
            currentVisibleFor?.co !== originalVisibility.co ||
            currentVisibleFor?.ct !== originalVisibility.ct;

          if (visibilityChanged) {
            hasChanges = true;
          }
        }
      }
    }

    if (this.hasChangesSubject.value !== hasChanges) {
      this.hasChangesSubject.next(hasChanges);
    }
    return hasChanges;
  }

  private validateTypeChange(newTypeValue: EntityScopeEnum) {
    if (this.lastValidatedTypeValue === newTypeValue) {
      return;
    }

    this.lastValidatedTypeValue = newTypeValue;

    if (this.dialogData.poolToEdit && newTypeValue === EntityScopeEnum.User) {
      const currentUser = this.dialogData.currentUser;
      const poolCreator = this.dialogData.poolToEdit.User;

      const isCreator = this.isCurrentUserCreator(currentUser, poolCreator);

      if (!isCreator) {
        this.addTypeValidationError();
      } else {
        this.removeTypeValidationError();
      }
    } else {
      this.removeTypeValidationError();
    }
  }

  private isCurrentUserCreator(currentUser: any, poolCreator: any): boolean {
    if (!currentUser || !poolCreator) {
      return false;
    }

    if (currentUser.Id && poolCreator.Id) {
      return currentUser.Id === poolCreator.Id;
    }

    if (currentUser.Account && poolCreator.Account) {
      return currentUser.Account === poolCreator.Account;
    }

    return false;
  }

  private addTypeValidationError() {
    const typeFieldConfig = this.formReadyEvent.dynamicFormService.getFieldConfig('type');
    if (typeFieldConfig) {
      typeFieldConfig.validators = [() => {
        return {
          mydocmaCustomError: this.translateService.instant('MM.pool.leftPane.form.typeUserCreatorValidationError'),
          mydocmaCustomErrorIcon: '_high-priority'
        };
      }];

      this.formReadyEvent.dynamicFormService.updateFieldConfig(typeFieldConfig);
    }
  }

  private removeTypeValidationError() {
    const typeFieldConfig = this.formReadyEvent.dynamicFormService.getFieldConfig('type');
    if (typeFieldConfig) {
      typeFieldConfig.validators = [];
      this.formReadyEvent.dynamicFormService.updateFieldConfig(typeFieldConfig);
    }
  }
}
